"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper, { type Area } from "react-easy-crop";
import { FormStatus, SubmitButton } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { FILETYPE_IMAGE, validateFiles } from "@/lib/utils/fileValidation";
import styles from "./AvatarUploadModal.module.css";

/**
 * Minimum image dimensions dla upload avatara. Sq miniatura to 120×120,
 * więc oryginał ≥ 240×240 daje zapas retina + crop margin (60-70% obrazu
 * idzie na crop area gdy aspect = 1, im więcej tym lepszy thumbnail).
 * Mirror backend `IAPI_userAvatarUpload` check (2006 throw).
 */
const MIN_AVATAR_DIMENSION = 240;

/**
 * AvatarUploadModal — 2-stage flow:
 *
 * 1. **Pick** — user wybiera plik (drag&drop lub file picker).
 *    Upload do `/api/iiapi/user/avatar` z multipart `avatar`. Backend
 *    zapisuje oryginał + auto-generuje miniatury s_/sq_ (center crop).
 *
 * 2. **Crop** (opcjonalne) — po upload pokazujemy oryginał w cropper UI
 *    (react-easy-crop, aspect 1:1). User może przesunąć/zoom region of
 *    interest. Click "Zastosuj kadrowanie" -> POST do `/api/iiapi/user/avatar/crop`
 *    z `{x, y, width, height}` w pikselach ORYGINAŁU.
 *
 * **Storage convention** (Imperial):
 *   - `/data/users/{id_domain_group}/{user_id}/avatar/{file.ext}` — oryginał
 *   - `s_{file.ext}` — 120px proporcjonalna
 *   - `sq_{file.ext}` — 120×120 kwadrat (sidebar avatar — to ten jest pokazywany)
 *
 * **Po success** -> `onSaved(newAvatarFilename)` — caller refetch'uje profile.
 */
export function AvatarUploadModal({
  currentAvatarUrl,
  currentOriginalUrl,
  onClose,
  onSaved,
  onDeleted,
}: AvatarUploadModalProps) {
  const [stage, setStage] = useState<Stage>("pick");
  const [busy, setBusy] = useState(false);
  /* Status text dla loading overlay — pokazuje co konkretnie robi backend
     (Wgrywanie / Przycinanie / Usuwanie / Ładowanie oryginału do re-crop). */
  const [busyStatus, setBusyStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Po upload: original_full_url do crop preview + original width/height
     (cropper potrzebuje natural dimensions żeby przeliczyć display ↔ original). */
  const [uploadedOriginal, setUploadedOriginal] = useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);

  /* Cropper state — przesunięcie + zoom. `croppedAreaPixels` zawiera coords
     w pikselach oryginału (gotowe do backend `IIMAGE::makeCrop`). */
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  /* Esc -> close. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  /* Hidden file input ref — file picker trigger przez button. */
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Mouse-down target tracking dla backdrop close — naprawia bug "drag-from-inside-out
     closes modal". Bez tego: drag selekcji tekstu z inputa poza modal -> mouseup
     na backdrop -> browser fires onClick na backdrop (common ancestor) -> close. */
  const backdropMouseDownTargetRef = useRef<EventTarget | null>(null);

  /* Portal mount — SSR-safe (createPortal nie działa server-side). useEffect
     ustawia `mounted=true` po hydration. Renderujemy do `document.body` żeby
     ominąć stacking context'y rodziców (sidebar ma `position: sticky` co
     tworzy nowy stacking context -> z-index modala nie wychodzi poza sidebar). */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  /* ─── Stage 1: upload ────────────────────────────────────────────────── */

  const handleFile = async (file: File) => {
    setError(null);

    /* Client-side guard — backend waliduje przez IVALIDATOR (rule 12), tu UX
       shortcut na shared spec FILETYPE_IMAGE zeby user nie czekal na backend
       odrzucenie dla SVG/BMP/TIFF/AVIF. Rozmiar nadal hardcoded bo backend
       limit (cfg()->upload['upload_max_filesize']) nie jest wystawiony do FE. */
    const { accepted, rejected } = validateFiles([file], FILETYPE_IMAGE);
    if (accepted.length === 0) {
      setError(rejected[0].reason);
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("Plik za duży — max 20 MB.");
      return;
    }

    /* Dimensions check — przed upload czytamy image przez Image() żeby sprawdzić
       naturalWidth/Height. Reject jeśli < MIN_AVATAR_DIMENSION (240) — backend
       odrzuciłby tak czy tak (2006), ale UX lepszy gdy user widzi to natychmiast
       (przed kosztownym upload). */
    setBusy(true);
    setBusyStatus("Sprawdzanie rozmiaru obrazu…");
    try {
      const dims = await readImageDimensions(file);
      if (
        dims.width < MIN_AVATAR_DIMENSION ||
        dims.height < MIN_AVATAR_DIMENSION
      ) {
        setError(
          `Obraz musi mieć minimum ${MIN_AVATAR_DIMENSION}×${MIN_AVATAR_DIMENSION} px (wgrany: ${dims.width}×${dims.height} px). Wczytaj większe zdjęcie.`,
        );
        setBusy(false);
        setBusyStatus(null);
        return;
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? `Nie udało się odczytać obrazu: ${e.message}`
          : "Nie udało się odczytać obrazu.",
      );
      setBusy(false);
      setBusyStatus(null);
      return;
    }

    setBusyStatus("Wgrywanie pliku…");
    try {
      const fd = new FormData();
      fd.append("avatar", file, file.name);
      const res = await fetch("/api/iiapi/user/avatar", {
        method: "POST",
        body: fd,
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        avatar?: AvatarResponse;
        message?: string;
      } | null;

      if (!res.ok || !body?.ok || !body.avatar) {
        setError(body?.message || "Nie udało się wgrać avatara.");
        setBusy(false);
        setBusyStatus(null);
        return;
      }

      /* Reset cropper state + przejdź do stage="crop". */
      setUploadedOriginal({
        url: body.avatar.original_full_url,
        width: body.avatar.width,
        height: body.avatar.height,
      });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setStage("crop");
      setBusy(false);
      setBusyStatus(null);
    } catch (e) {
      setError(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      setBusy(false);
      setBusyStatus(null);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    /* Reset input żeby ten sam plik mógł być wybrany ponownie. */
    e.target.value = "";
  };

  /* ─── Stage 2: crop ──────────────────────────────────────────────────── */

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels || !uploadedOriginal) return;
    setError(null);

    /* Clamp crop coords do bounds obrazu — react-easy-crop ma znany bug dla
       bardzo małych obrazków (np. 78×210): zwraca coords poza image bounds
       (x=78 na srcW=78 = przy prawej krawędzi, brak miejsca na width>0).
       Backend by też clamp'ował przez IIMAGE::makeCrop, ale teraz throw'uje
       gdy width/height po clamp ≤ 0 — wolimy złapać tu z bardziej user-friendly
       message niż dostać raw IIMAGE exception. */
    const srcW = uploadedOriginal.width;
    const srcH = uploadedOriginal.height;
    let x = Math.round(croppedAreaPixels.x);
    let y = Math.round(croppedAreaPixels.y);
    let w = Math.round(croppedAreaPixels.width);
    let h = Math.round(croppedAreaPixels.height);

    /* x/y do [0, src-1], width/height do mieści się w bounds. */
    x = Math.max(0, Math.min(x, Math.max(0, srcW - 1)));
    y = Math.max(0, Math.min(y, Math.max(0, srcH - 1)));
    w = Math.max(1, Math.min(w, srcW - x));
    h = Math.max(1, Math.min(h, srcH - y));

    if (w < 10 || h < 10) {
      setError(
        `Wybrany kadr jest za mały (${w}×${h}px). Obraz oryginalny ma ${srcW}×${srcH}px — wybierz większy region lub wczytaj większe zdjęcie.`,
      );
      return;
    }

    setBusy(true);
    setBusyStatus("Przycinanie i generowanie miniatur…");

    try {
      const res = await fetch("/api/iiapi/user/avatar/crop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          crop: { x, y, width: w, height: h },
        }),
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        avatar?: AvatarResponse;
        message?: string;
      } | null;

      if (!res.ok || !body?.ok || !body.avatar) {
        setError(body?.message || "Nie udało się przyciąć avatara.");
        setBusy(false);
        setBusyStatus(null);
        return;
      }

      onSaved(body.avatar.filename, body.avatar.sq_full_url);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      setBusy(false);
      setBusyStatus(null);
    }
  };

  /* Skip crop — użyj domyślnego center crop wygenerowanego przy upload. */
  const handleSkipCrop = () => {
    if (!uploadedOriginal) return;
    /* Po upload backend już zapisał i wygenerował sq_/s_. Wystarczy zamknąć
       modal i odświeżyć profil. */
    onSaved("", ""); //caller refetch'uje profile dla świeżych URLs
    onClose();
  };

  /* ─── Delete current ─────────────────────────────────────────────────── */

  const handleDelete = async () => {
    /* confirmDialog — branded modal (zob. lib/notifications/confirmDialog.ts)
       z `variant: 'danger'` dla destructive action. Returns Promise<boolean>. */
    const ok = await confirmDialog({
      title: "Usunąć avatar?",
      message: "Avatar zostanie trwale usunięty wraz z miniaturami. Nie można cofnąć tej operacji.",
      variant: "danger",
      confirmLabel: "Usuń",
      cancelLabel: "Anuluj",
    });
    if (!ok) return;

    setError(null);
    setBusy(true);
    setBusyStatus("Usuwanie avatara…");
    try {
      const res = await fetch("/api/iiapi/user/avatar", { method: "DELETE" });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
      } | null;

      if (!res.ok || !body?.ok) {
        setError(body?.message || "Nie udało się usunąć avatara.");
        setBusy(false);
        setBusyStatus(null);
        return;
      }
      onDeleted();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      setBusy(false);
      setBusyStatus(null);
    }
  };

  /**
   * Re-crop existing avatar — pomija upload, ładuje obecny oryginał i przechodzi
   * do stage="crop". Wymaga `currentOriginalUrl` (przekazane gdy user MA już
   * avatar). Image preloaduje przez `new Image()` żeby pozyskać natural
   * dimensions (potrzebne dla Cropper'a do skalowania crop coords back
   * to original pixels).
   */
  const handleRecropExisting = async () => {
    if (!currentOriginalUrl) return;
    setError(null);
    setBusy(true);
    setBusyStatus("Ładowanie obrazu do ponownego przycięcia…");

    try {
      const dims = await new Promise<{ width: number; height: number }>(
        (resolve, reject) => {
          const img = new Image();
          img.onload = () =>
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => reject(new Error("Nie udało się załadować obrazu."));
          img.src = currentOriginalUrl;
        },
      );

      setUploadedOriginal({
        url: currentOriginalUrl,
        width: dims.width,
        height: dims.height,
      });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setStage("crop");
      setBusy(false);
      setBusyStatus(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nie udało się załadować obrazu.");
      setBusy(false);
      setBusyStatus(null);
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        backdropMouseDownTargetRef.current = e.target;
      }}
      onClick={(e) => {
        /* Close TYLKO gdy OBA — mousedown i click target — to backdrop sam.
           Drag tekstu z inputa do backdrop'a -> mousedown był na inpucie -> skip. */
        if (
          e.target === e.currentTarget &&
          backdropMouseDownTargetRef.current === e.currentTarget &&
          !busy
        ) {
          onClose();
        }
        backdropMouseDownTargetRef.current = null;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Upload avatara"
    >
      <div className={styles.modal}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            {stage === "pick" ? "Wgraj avatar" : "Dostosuj kadrowanie"}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={busy}
            aria-label="Zamknij"
          >
            <Icon name="x" />
          </button>
        </header>

        <div className={styles.body}>
          {/* Loading overlay — full-modal spinner + status message podczas
              każdej async operacji (upload, crop, delete, re-crop preload).
              Sygnalizuje że backend pracuje. `aria-busy` na modalu = screen
              reader announces. */}
          {busy && (
            <div className={styles.loadingOverlay} aria-busy="true" role="status">
              <div className={styles.spinner} aria-hidden="true" />
              <p className={styles.loadingText}>
                {busyStatus ?? "Przetwarzanie…"}
              </p>
            </div>
          )}

          {stage === "pick" && (
            <PickStage
              currentAvatarUrl={currentAvatarUrl}
              currentOriginalUrl={currentOriginalUrl}
              busy={busy}
              fileInputRef={fileInputRef}
              onPickClick={() => fileInputRef.current?.click()}
              onFileChange={handleFileInputChange}
              onDelete={handleDelete}
              onFileDrop={handleFile}
              onRecropExisting={handleRecropExisting}
            />
          )}

          {stage === "crop" && uploadedOriginal && (
            <CropStage
              imageUrl={uploadedOriginal.url}
              crop={crop}
              zoom={zoom}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}

          {error && <FormStatus variant="error">{error}</FormStatus>}
        </div>

        <footer className={styles.footer}>
          {stage === "pick" && (
            <button
              type="button"
              className={styles.linkBtn}
              onClick={onClose}
              disabled={busy}
            >
              Anuluj
            </button>
          )}
          {stage === "crop" && (
            <>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleSkipCrop}
                disabled={busy}
              >
                Pomiń (użyj automatycznego)
              </button>
              <SubmitButton loading={busy} onClick={handleApplyCrop}>
                Zastosuj kadrowanie
              </SubmitButton>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Read image natural dimensions z File object (przez `URL.createObjectURL` +
 * `new Image()`). Promise resolves z `{width, height}` lub rejects z error.
 *
 * Używane do client-side validation min size PRZED uploadem (UX shortcut —
 * backend i tak waliduje, ale tu user widzi natychmiast).
 */
function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Plik nie jest poprawnym obrazem."));
    };
    img.src = url;
  });
}

type Stage = "pick" | "crop";

type AvatarResponse = {
  filename: string;
  original_url: string;
  sq_url: string;
  s_url: string;
  original_full_url: string;
  sq_full_url: string;
  s_full_url: string;
  width: number;
  height: number;
};

export type AvatarUploadModalProps = {
  /** SQ thumbnail URL — podgląd w stage "pick". Null gdy brak avatara. */
  currentAvatarUrl: string | null;
  /** Original (pełnowymiarowy) avatar URL — używany do "Przytnij ponownie"
      funkcji (re-crop bez ponownego uploadu). Null gdy brak avatara. */
  currentOriginalUrl: string | null;
  onClose: () => void;
  /** Wywoływane po zapisie (upload lub crop) — caller refetch profile. */
  onSaved: (filename: string, sqFullUrl: string) => void;
  /** Po usunięciu avatara. */
  onDeleted: () => void;
};

/* ─── Stage: pick file ──────────────────────────────────────────────────── */

function PickStage({
  currentAvatarUrl,
  currentOriginalUrl,
  busy,
  fileInputRef,
  onPickClick,
  onFileChange,
  onDelete,
  onFileDrop,
  onRecropExisting,
}: {
  currentAvatarUrl: string | null;
  currentOriginalUrl: string | null;
  busy: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPickClick: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete: () => void;
  onFileDrop: (file: File) => void;
  onRecropExisting: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileDrop(file);
  };

  return (
    <div className={styles.pickContainer}>
      {currentAvatarUrl && (
        <div className={styles.currentPreview}>
          <p className={styles.previewLabel}>Aktualny avatar:</p>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={currentAvatarUrl} alt="" className={styles.currentImg} />
        </div>
      )}

      {/* Re-crop button — ZAWSZE gdy user ma już oryginalne zdjęcie (avatar).
          Pomija upload (oryginał jest na backendzie) i ładuje do crop'era
          żeby user mógł zmienić kadrowanie bez ponownego wyboru pliku. */}
      {currentOriginalUrl && (
        <button
          type="button"
          className={styles.recropBtn}
          onClick={onRecropExisting}
          disabled={busy}
        >
          <Icon name="pencil-simple" /> Przytnij ponownie obecne zdjęcie
        </button>
      )}

      <div
        className={`${styles.dropZone}${dragOver ? ` ${styles.dropZoneActive}` : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={onPickClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onPickClick();
        }}
        aria-label="Wybierz plik avatara"
      >
        <Icon name="image" className={styles.dropIcon} />
        <p className={styles.dropText}>
          {currentAvatarUrl
            ? "Lub wgraj nowe zdjęcie (JPG/PNG/GIF/WEBP, max 20 MB)"
            : "Kliknij lub przeciągnij plik (JPG/PNG/GIF/WEBP, max 20 MB)"}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILETYPE_IMAGE.accept}
          onChange={onFileChange}
          className={styles.hiddenInput}
          disabled={busy}
        />
      </div>

      {currentAvatarUrl && (
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onDelete}
          disabled={busy}
        >
          <Icon name="trash" /> Usuń obecny avatar
        </button>
      )}
    </div>
  );
}

/* ─── Stage: crop ───────────────────────────────────────────────────────── */

function CropStage({
  imageUrl,
  crop,
  zoom,
  onCropChange,
  onZoomChange,
  onCropComplete,
}: {
  imageUrl: string;
  crop: { x: number; y: number };
  zoom: number;
  onCropChange: (c: { x: number; y: number }) => void;
  onZoomChange: (z: number) => void;
  onCropComplete: (cropped: Area, areaPixels: Area) => void;
}) {
  /* Preload diagnostic — react-easy-crop renderuje czarne tło gdy image fail
     loaduje, bez visible error. Preload przez `new Image()` daje konkretny
     error message (404/network). Stan `loadError` pokazuje banner.
     **Bez `crossOrigin`** — backend nie wystawia CORS headers dla statics,
     ale `<img>` cross-origin display nie wymaga CORS (tylko canvas read).
     Cropper sam też nie używa canvas dla display — tylko `<img>` overlay. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    const img = new Image();
    img.onload = () => setLoading(false);
    img.onerror = () =>
      setLoadError(
        `Nie udało się załadować obrazu (${imageUrl}). Sprawdź konsolę przeglądarki — 404 / network / CSP.`,
      );
    img.src = imageUrl;
  }, [imageUrl]);

  return (
    <div className={styles.cropContainer}>
      <div className={styles.cropArea}>
        {loadError ? (
          <div className={styles.cropError}>
            <Icon name="warning-circle" />
            <span>{loadError}</span>
          </div>
        ) : loading ? (
          <div className={styles.cropLoading}>Ładowanie obrazu…</div>
        ) : (
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropComplete}
          />
        )}
      </div>
      <div className={styles.zoomControls}>
        <label className={styles.zoomLabel}>
          Zoom
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            className={styles.zoomSlider}
          />
        </label>
      </div>
    </div>
  );
}
